/* eslint-disable max-len */
import React, { useContext, MouseEvent } from 'react';
import classNames from 'classnames';
import { JobSearchContext } from '../../context';
import AutocompleteBootstrap5 from '../../../components/react/autocompletes/autocomplete_bootstrap_5';
import {
  autocompletePlacesPath,
  autocompleteJobsEmployerCompaniesPath,
} from '../../../utilities/api_relative_paths';
import TypeaheadSelectBootstrap5 from '../../../components/react/autocompletes/typeahead_select_bootstrap_5';
import Checkbox from '../../../components/react/checkbox/checkbox';
import Tooltip from '../../../components/react/tooltip/tooltip';
import {
  PUBLICATION_INTERVAL_OPTIONS,
  NEWS_TOPIC_OPTIONS,
} from '../../constants/job_search.constants';
import { STAGE_OPTIONS } from '../../../utilities/constants/job_related.constants';
import { Option } from '../../../components/react/autocompletes/types';
import { useClearFilters } from '../../hooks';
import typeaheadStyles from '../../../components/react/autocompletes/generic_typeahead.module.scss';
import './filter_list.scss';
/* eslint-enable max-len */

const FilterList = () => {
  const {
    userInfo: { isSignedIn },
    setKeyword,
    keywordUserInput,
    setKeywordUserInput,
    careerLevels,
    contractLengths,
    setPageNumber,
    locations,
    setLocations,
    jobLevels,
    setJobLevels,
    newsTopics,
    setNewsTopics,
    isRemote,
    setIsRemote,
    hasSalaryData,
    setHasSalaryData,
    type,
    setType,
    publicationInterval,
    setPublicationInterval,
    positionTypes,
    setPositionTypes,
    orgNames,
    setOrgNames,
    stages,
    setStages,
    newsTopicAggregations = [],
    isLoading,
    showAlerts,
    setShowAlerts,
  } = useContext(JobSearchContext);

  const clearFilters = useClearFilters();

  const onKeywordSearch = (event: MouseEvent) => {
    event.preventDefault();
    setPageNumber(1);
    // If the input value is empty, remove URL filter keywords (filter[keywords][])
    setKeyword(keywordUserInput.trim().length ? keywordUserInput : undefined);
  };

  const renderKeywordInput = (
    <div className='pe-xl-0'>
      <label className='form-label' htmlFor='jobSearchKeyword'>
        Keywords
      </label>
      <div className='input-group'>
        <input
          id='jobSearchKeyword'
          data-testid='jobs-search-keywords-filter'
          className='form-control keyword-filter rounded-1'
          type='text'
          name='keywords'
          aria-label='keyword search'
          placeholder='e.g. Chief of party'
          value={keywordUserInput}
          onChange={({ target }) => setKeywordUserInput(target.value)}
        />
      </div>
    </div>
  );

  const renderLocationsInput = (
    <div className='pe-xl-0'>
      <label className='form-label' htmlFor='jobSearchLocations'>
        Location
      </label>
      <AutocompleteBootstrap5
        id='jobSearchLocations'
        inputProps={{
          'aria-label': 'jobSearchLocations',
          'data-testid': 'jobs-search-location-filter',
          className: 'form-control',
        }}
        apiPath={autocompletePlacesPath}
        minLength={2}
        multiple={true}
        placeholder='e.g. Worldwide'
        selected={locations || []}
        filterBy={(option) => {
          if (!locations?.length) return true;
          return locations.every((element) => element !== option.text);
        }}
        onSelect={(options) => {
          setPageNumber(1);
          setLocations(options.map((entry) => entry?.text || entry));
        }}
      />
    </div>
  );

  const renderSearchButton = (
    <div className='d-none d-xl-grid button-container'>
      <button
        className='btn btn-primary'
        aria-label='keyword search'
        type='submit'
        tabIndex={-1}
        data-testid='jobs-search-submit-button'
        onClick={onKeywordSearch}
      >
        <i className='fal icon-search me-1' />
        Search
      </button>
    </div>
  );

  const renderJobLevelInput = (
    <div className='col-12 col-md-4 pe-xl-0'>
      <label className='form-label' htmlFor='jobSearchJobLevel'>
        Experience level
      </label>
      <TypeaheadSelectBootstrap5
        id='jobSearchJobLevel'
        inputProps={{
          'aria-label': 'jobSearchJobLevel',
          className: 'form-control',
        }}
        multiple={true}
        placeholder='All levels'
        onChange={(options: string[]) => {
          if (isLoading) return;

          setPageNumber(1);
          setJobLevels(options);
        }}
        options={careerLevels}
        selected={careerLevels.filter((entry) => jobLevels?.includes(entry))}
      />
    </div>
  );

  const renderPositionTypeFilter = (
    <div className='col-12 col-md-4 pe-xl-0'>
      <label className='form-label' htmlFor='jobSearchPositionType'>
        Contract type
      </label>
      <TypeaheadSelectBootstrap5
        id='jobSearchPositionType'
        inputProps={{
          'aria-label': 'jobSearchPositionType',
          className: 'form-control',
        }}
        multiple={true}
        placeholder='All positions'
        onChange={(options: string[]) => {
          if (isLoading) return;

          setPageNumber(1);
          setPositionTypes(options);
        }}
        options={contractLengths}
        selected={contractLengths.filter((entry) => positionTypes?.includes(entry))}
      />
    </div>
  );

  const renderPublicationIntervalInput = (
    <div className='col-12 col-md-4'>
      <label className='form-label' htmlFor='jobSearchPostedDate'>
        Posted date
      </label>
      <TypeaheadSelectBootstrap5
        id='jobSearchPostedDate'
        inputProps={{
          'aria-label': 'jobSearchPostedDate',
          className: classNames(
            'form-control rounded-1',
            { [typeaheadStyles.activeField]: publicationInterval }
          ),
        }}
        disabled={isLoading}
        placeholder='All time'
        onChange={(selectedOptions: Option[]) => {
          setPageNumber(1);
          setPublicationInterval(selectedOptions[0]?.value);
        }}
        options={PUBLICATION_INTERVAL_OPTIONS}
        selected={PUBLICATION_INTERVAL_OPTIONS.filter(
          (entry) => entry.value === publicationInterval
        )}
        filterBy={(option: { text: string }, data) => {
          if (publicationInterval) return true;
          return option.text.toLowerCase().includes(data.text.toLowerCase());
        }}
      />
    </div>
  );

  const renderSectorFilter = (
    <div className='col-12 col-md-4 pe-xl-0'>
      <label className='form-label' htmlFor='jobSearchNewsSector'>
        Sector
      </label>
      <TypeaheadSelectBootstrap5
        id='jobSearchNewsSector'
        inputProps={{
          'aria-label': 'jobSearchNewsSector',
          className: 'form-control',
        }}
        multiple={true}
        placeholder='All sectors'
        onChange={(selectedOptions) => {
          if (isLoading) return;

          setPageNumber(1);
          setNewsTopics(selectedOptions.map((entry) => entry.value || entry));
        }}
        renderMenuItemChildren={(option) => {
          const number =
            newsTopicAggregations?.find((topic) => topic.key === option.value)?.value || 0;
          return (
            <>
              {option.text} ({number})
            </>
          );
        }}
        options={NEWS_TOPIC_OPTIONS}
        selected={NEWS_TOPIC_OPTIONS.filter((entry) => newsTopics?.includes(entry.value))}
      />
    </div>
  );

  const renderOrganizationInput = (
    <div className='col-12 col-md-5 pe-xl-0'>
      <label className='form-label' htmlFor='jobSearchOrganization'>
        Organization
      </label>
      <AutocompleteBootstrap5
        id='jobSearchOrganization'
        inputProps={{
          'aria-label': 'jobSearchOrganization',
          className: 'form-control',
        }}
        placeholder='e.g. Devex'
        minLength={2}
        multiple={true}
        apiPath={autocompleteJobsEmployerCompaniesPath}
        selected={orgNames || []}
        filterBy={(option) => {
          if (!orgNames?.length) return true;
          return orgNames.every((element) => element !== option.text);
        }}
        onSelect={(options) => {
          setPageNumber(1);
          setOrgNames(options.map((entry) => entry?.text || entry));
        }}
      />
    </div>
  );

  const renderJobStageInput = (
    <div className='col-12 col-md-3' id='jobSearchStageInput'>
      <label className='form-label' htmlFor='jobSearchStage'>
        Project stage
      </label>
      <TypeaheadSelectBootstrap5
        id='jobSearchStage'
        inputProps={{
          'aria-label': 'jobSearchStage',
          className: 'form-control',
        }}
        multiple={true}
        renderMenuItemChildren={(option) => {
          return (
            <Tooltip placement='top' message={option.tooltip}>
              <span>
                <i className='text-gray-light fal icon-info-circle small me-2' />
                <span>{option.value}</span>
              </span>
            </Tooltip>
          );
        }}
        placeholder='All stages'
        labelKey='value'
        onChange={(selectedOptions) => {
          if (isLoading) return;

          setPageNumber(1);
          setStages(selectedOptions.map((entry) => entry.value || entry));
        }}
        options={STAGE_OPTIONS}
        selected={STAGE_OPTIONS.filter((entry) => stages?.includes(entry.value))}
      />
    </div>
  );

  const remoteOnlyToggle = (
    <div className='d-flex flex-column flex-sm-row'>
      <div className='align-self-center'>
        <Checkbox
          ariaLabel='remote only toggle'
          dataTestId='remote-jobs-toggle'
          isSlider={true}
          isToggled={JSON.parse(isRemote)}
          handleChange={() => {
            setPageNumber(1);
            setIsRemote(String(!JSON.parse(isRemote)));
          }}
          disabled={isLoading}
        />
      </div>
      <small className='ms-1 mb-1 align-self-center'>Remote</small>
    </div>
  );

  const forLoggedInBadge = (
    <div className='for-logged-in-badge d-flex justify-content-center align-items-center rounded'>
      <span className='fw-bold text-black'>For logged in users</span>
    </div>
  );

  const withSalaryOnlyToggle = (
    <div className='d-flex flex-column flex-sm-row ms-2 ms-lg-4 position-relative'>
      <div className='align-self-center'>
        <Checkbox
          ariaLabel='with salary data only toggle'
          dataTestId='with-salary-jobs-toggle'
          isSlider={true}
          isToggled={JSON.parse(hasSalaryData)}
          handleChange={() => {
            setPageNumber(1);
            setHasSalaryData(String(!JSON.parse(hasSalaryData)));
          }}
          d-flex
          flex-column
          flex-xl-row
          disabled={!isSignedIn || isLoading}
        />
      </div>
      <small className='ms-1 mb-1 align-self-center'>With salary</small>
      {!isSignedIn && forLoggedInBadge}
    </div>
  );

  const exclusiveJobsOnlyToggle = (
    <div className='d-flex flex-column flex-sm-row ms-2 ms-lg-4'>
      <div className='align-self-center'>
        <Checkbox
          ariaLabel='exclusive jobs only toggle'
          dataTestId='exclusive-jobs-toggle'
          isSlider={true}
          isToggled={type === 'exclusive jobs'}
          handleChange={() => {
            setPageNumber(1);
            if (type === 'exclusive jobs') return setType('');
            setType('exclusive jobs');
          }}
          disabled={isLoading}
        />
      </div>
      <small className='ms-1 mb-1 align-self-center'>Exclusive jobs</small>
    </div>
  );

  const alertsButton = (
    <Tooltip
      placement='top'
      message='For logged in users'
      show={isSignedIn ? false : undefined}
      id='alerts-button'
    >
      <button
        type='button'
        className='btn btn-outline-info w-100 p-0'
        aria-controls='alerts-container'
        data-testid='alerts-button'
        onClick={() => setShowAlerts(!showAlerts)}
        disabled={!isSignedIn}
      >
        <i className='fal icon-bell me-2' />
        <span>Alerts</span>
        <i
          className={classNames('fal toggle ms-2', {
            'icon-angle-down': !showAlerts,
            'icon-angle-up': showAlerts,
          })}
        />
      </button>
    </Tooltip>
  );

  const clearFiltersButton = (
    <button
      type='button'
      className='btn btn-secondary w-100 ms-2'
      data-testid='clear-filters-button'
      onClick={clearFilters}
      disabled={isLoading}
    >
      Clear filters
    </button>
  );

  return (
    <div className='filter-list px-3 pb-3' data-testid='filters'>
      <form aria-label='Job search form' name='Job search'>
        <fieldset className='p-0 m-0 border-0' aria-label='Job search'>
          <div className='first-row-container d-grid mb-3'>
            {renderKeywordInput}
            {renderLocationsInput}
            {renderSearchButton}
          </div>
          <div className='row mb-3'>
            {renderJobLevelInput}
            {renderPositionTypeFilter}
            {renderPublicationIntervalInput}
          </div>
          <div className='row mb-3'>
            {renderSectorFilter}
            {renderOrganizationInput}
            {renderJobStageInput}
          </div>
          <div className='row'>
            <div className='col-12 col-xl-8 d-flex justify-content-around justify-content-xl-start'>
              {remoteOnlyToggle}
              {withSalaryOnlyToggle}
              {exclusiveJobsOnlyToggle}
            </div>
            <div className='col-12 d-flex d-xl-none my-2'>{renderSearchButton}</div>
            {/* eslint-disable-next-line max-len */}
            <div className='col-12 col-xl-4 d-flex py-2 position-relative filter-list-search-buttons'>
              {alertsButton}
              {clearFiltersButton}
            </div>
          </div>
        </fieldset>
      </form>
    </div>
  );
};

export default FilterList;
